import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { tokens } from './tokens';
import { useSettingsStore } from '@/features/settings/settingsStore';

export type Scheme = 'light' | 'dark';
export type ThemeColors = { [K in keyof typeof tokens.dark]: string };

type AppThemeCtx = { scheme: Scheme; colors: ThemeColors; isDark: boolean };

const ThemeCtx = createContext<AppThemeCtx | null>(null);

/** `useColorScheme()`'s web equivalent — the OS appearance, live. */
function useOSScheme(): Scheme {
  const [scheme, setScheme] = useState<Scheme>(() =>
    globalThis.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark',
  );
  useEffect(() => {
    const mq = globalThis.matchMedia?.('(prefers-color-scheme: light)');
    if (!mq) return undefined;
    const onChange = (e: MediaQueryListEvent) => setScheme(e.matches ? 'light' : 'dark');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return scheme;
}

/**
 * Resolves the user's `themePreference` ('system' | 'light' | 'dark') against the OS
 * appearance when set to 'system', and provides the result via context. Nest with
 * `force` (see `ImmersiveDark`) to pin a subtree to a scheme regardless of the
 * ambient preference — the nearest provider always wins.
 *
 * The ROOT provider (the one without `force`) also publishes the resolved palette
 * to CSS custom properties on <html> and stamps `data-theme`, so plain CSS
 * (global.css, ::selection, scrollbars, the page background behind the app shell)
 * can reach the same colours the JS side uses. Nested/forced providers deliberately
 * do NOT touch the document — they only re-point the context for their subtree.
 */
export function AppThemeProvider({
  children,
  force,
  root = false,
}: {
  children: ReactNode;
  force?: Scheme;
  root?: boolean;
}) {
  const osScheme = useOSScheme();
  const preference = useSettingsStore((s) => s.themePreference);

  const scheme: Scheme = useMemo(() => {
    if (force) return force;
    if (preference === 'system') return osScheme;
    return preference;
  }, [force, preference, osScheme]);

  useLayoutEffect(() => {
    if (!root) return;
    applySchemeToDocument(scheme);
  }, [root, scheme]);

  const value = useMemo<AppThemeCtx>(
    () => ({ scheme, colors: tokens[scheme], isDark: scheme === 'dark' }),
    [scheme],
  );

  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useAppTheme(): AppThemeCtx {
  const ctx = useContext(ThemeCtx);
  if (!ctx) {
    throw new Error('useAppTheme() must be used within <AppThemeProvider> (mounted in Providers.tsx)');
  }
  return ctx;
}

/**
 * Wrap an immersive/brand subtree (onboarding, live match, wallet passport, brand
 * hero moments) to force dark regardless of the user's global theme preference.
 */
export function ImmersiveDark({ children }: { children: ReactNode }) {
  return <AppThemeProvider force="dark">{children}</AppThemeProvider>;
}

/** camelCase palette key -> `--c-kebab-case` custom property. */
const cssVarName = (key: string) => `--c-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}`;

/** Every palette role, as a CSS custom property name. Exported for global.css parity. */
export const COLOR_VARS = Object.keys(tokens.dark).map(cssVarName);

function applySchemeToDocument(scheme: Scheme) {
  const el = document.documentElement;
  const palette = tokens[scheme] as Record<string, string>;
  for (const [key, value] of Object.entries(palette)) {
    el.style.setProperty(cssVarName(key), value);
  }
  // Fixed (scheme-independent) brand values, so CSS can use them too.
  el.style.setProperty('--c-mint', tokens.accentMint);
  el.style.setProperty('--c-gold', tokens.gold);
  el.style.setProperty('--c-brand-green', tokens.brand.green);
  el.style.setProperty('--c-live', tokens.semantic.live);
  el.style.setProperty('--c-positive', tokens.semantic.positive);
  el.style.setProperty('--c-negative', tokens.semantic.negative);
  el.dataset.theme = scheme;
  el.style.colorScheme = scheme;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', palette.appBg);
}
